import { FC, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { Button, ButtonVariant } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import { close } from '@purpur/library/icon/assets/close';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { KeyboardKey } from '@telialt/common-constants/src/keyboard-keys';

import { SEARCH_TEST_ID } from '../../test/test-ids';
import {
    DisplayContainer,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
    DrawerWrapper,
    Overlay,
    XCloseButton,
} from './FacetsDrawer.styles';
import { VisibilityVariant } from './model';

type CustomButton = { title?: string; variant?: ButtonVariant };

type CustomizationProps = {
    title?: string;
    visibilityVariant?: VisibilityVariant;
    closeButton?: CustomButton;
    clearButton?: CustomButton;
};

type Props = {
    id: string;
    triggerButton: (onClick: () => void, isOpen: boolean) => ReactNode;
    onClear: () => void;
    children: ReactNode;
    dataTest?: string;
    className?: string;
    triggerButtonRef?: RefObject<HTMLButtonElement>;
} & CustomizationProps;

export const FacetsDrawer: FC<Props> = ({
    id,
    triggerButtonRef,
    triggerButton,
    onClear,
    children,
    dataTest,
    className,
    title,
    visibilityVariant = 'mobile',
    closeButton,
    clearButton,
}) => {
    const { t } = useTranslations();

    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const drawerRef = useRef(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const handleClose = useCallback(() => {
        setIsOpen(false);

        const activeElement = document.activeElement;
        const isKeyboardInitiated = activeElement && drawerRef.current?.contains(activeElement);

        if (isKeyboardInitiated) {
            triggerButtonRef?.current?.focus();
        }
    }, [triggerButtonRef]);

    const handleOpen = () => {
        setIsVisible(true);

        const isKeyboardInitiated = document.activeElement === triggerButtonRef?.current;

        if (isKeyboardInitiated) {
            window.setTimeout(() => closeButtonRef.current?.focus());
        }
    };

    const handleClear = () => {
        onClear();
        handleClose();
    };

    const handleTransitionEnd = () => {
        if (!isOpen) {
            setIsVisible(false);
        }
    };

    useEffect(() => {
        if (isVisible) {
            window.setTimeout(() => setIsOpen(true), 50);
        }
    }, [isVisible]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (drawerRef.current && !drawerRef.current.contains(event.target)) {
                handleClose();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === KeyboardKey.Escape) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleClose]);

    return (
        <DisplayContainer $visibility={visibilityVariant} className={className}>
            {triggerButton(handleOpen, isOpen)}
            {isVisible && <Overlay data-open={isOpen} />}
            <DrawerWrapper
                id={id}
                $isOpen={isOpen}
                $isVisible={isVisible}
                ref={drawerRef}
                onTransitionEnd={handleTransitionEnd}
                data-test={dataTest}
                aria-labelledby="facets-drawer-title"
            >
                <DrawerHeader>
                    <DrawerTitle id="facets-drawer-title">
                        {title ?? t['search.component.facets_drawer.heading']}
                    </DrawerTitle>
                    <XCloseButton
                        ref={closeButtonRef}
                        variant="text"
                        iconOnly={true}
                        aria-label={t['search.component.facets_drawer.close_button.aria_label']}
                        onClick={handleClose}
                        data-test={SEARCH_TEST_ID.FACETS_DRAWER_CLOSE_BUTTON}
                    >
                        <Icon svg={close} size="md" />
                    </XCloseButton>
                </DrawerHeader>
                <DrawerContent>{children}</DrawerContent>
                <DrawerFooter>
                    <Button
                        variant={closeButton?.variant ?? 'primary'}
                        onClick={handleClose}
                        data-test={SEARCH_TEST_ID.FACETS_DRAWER_FILTER_BUTTON}
                    >
                        {closeButton?.title ?? t['search.component.facets_drawer.filter_button']}
                    </Button>
                    <Button
                        variant={clearButton?.variant ?? 'secondary'}
                        onClick={handleClear}
                        data-test={SEARCH_TEST_ID.FACETS_DRAWER_CLEAR_BUTTON}
                    >
                        {clearButton?.title ?? t['search.component.facets_drawer.delete_filters']}
                    </Button>
                </DrawerFooter>
            </DrawerWrapper>
        </DisplayContainer>
    );
};
